import { useEffect, useMemo } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';

import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_INSTANT_DEPOSIT_LIMIT } from '#client/queries';

export default function useInstantDepositLimit() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { isEnabled, isEnrolled, isValidMfa } = useUnitAccountId();
  const [isShowInstantDepositFlagEnabled, isShowInstantDepositFlagLoading] = useFeatureFlag(
    RELEASE_SHOW_INSTANT_DEPOSIT
  );
  const { createCommonErrorToast } = useToastContext();

  const isSkip =
    isShowInstantDepositFlagLoading ||
    !isShowInstantDepositFlagEnabled ||
    !isEnabled ||
    !isEnrolled ||
    !isValidMfa;

  const {
    error,
    loading,
    data: originalData,
    refetch: refetchInstantDepositLimit,
    errors
  } = useQuery(GET_INSTANT_DEPOSIT_LIMIT, {
    variables: {
      businessEntityUuid
    },
    skip: isSkip,
    noThrow: true,
    skipCache: true
  });

  useLogError(errors, error);

  const data = useMemoObject(originalData);

  const hasLimit = !!data && !!data?.instantFundingAccounts?.[0]?.transactionMetrics;

  // The remaining money of the astra account
  const remains = useMemo(() => {
    if (hasLimit) {
      const { dailyTransferLimit, monthlyTransferLimit, dailyUsageTotal, monthlyUsageTotal } =
        data.instantFundingAccounts[0].transactionMetrics;
      return {
        dailyRemain: dailyTransferLimit.amount - dailyUsageTotal.amount,
        monthlyRemain: monthlyTransferLimit.amount - monthlyUsageTotal.amount
      };
    }
    return {};
  }, [data, hasLimit]);

  // The astra account money limit for instant deposit
  const limit = useMemo(() => {
    if (hasLimit) {
      const { dailyTransferLimit, monthlyTransferLimit, perTransferLimit, minimumTransferLimit } =
        data.instantFundingAccounts[0].transactionMetrics;

      return {
        perTransferLimit: perTransferLimit.amount,
        dailyTransferLimit: dailyTransferLimit.amount,
        monthlyTransferLimit: monthlyTransferLimit.amount,
        minimumTransferLimit: minimumTransferLimit.amount
      };
    }
    return {};
  }, [data, hasLimit]);

  // Metrics on transfer count
  const transfers = useMemo(() => {
    if (hasLimit) {
      const { dailyTransferCount, dailyTransferCountLimit } =
        data.instantFundingAccounts[0].transactionMetrics;

      const isDailyTransferLimitExceeded = dailyTransferCount >= dailyTransferCountLimit;
      return {
        dailyTransferCount,
        dailyTransferCountLimit,
        isDailyTransferLimitExceeded
      };
    }
    return {};
  }, [data, hasLimit]);

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return {
    loading,
    hasLimit,
    limit,
    remains,
    transfers,
    refetchInstantDepositLimit,
    error
  };
}
